import React, { useState, useEffect, useCallback } from "react";
import { Button, Table, Badge, Form, Alert, Modal, Row, Col } from "react-bootstrap";
import axios from "axios";
import config from "../../../config";
import { toast } from "react-toastify";
import { useHasPermission, PERMISSIONS } from "../../../utils/permissions";
import PermissionGate from "../../common/PermissionGate";

const GRNStatusBadge = ({ status }) => {
  const variants = {
    'Draft': 'secondary',
    'Confirmed': 'primary',
    'Approved': 'success',
    'Cancelled': 'danger'
  };
  return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
};

const GRNItemsTable = ({ items, grnId, onUpdate }) => {
  return (
    <Table bordered hover responsive size="sm" className="mt-3">
      <thead className="table-light">
        <tr>
          <th>Packaging Material</th>
          <th>Ordered Qty</th>
          <th>Unloaded Qty</th>
          {/* <th>Quality Status</th> */}
          <th>Remarks</th>
        </tr>
      </thead>
      <tbody>
        {items?.map((item, index) => (
          <tr key={index}>
            <td>
              <div>
                <strong>{item.packingMaterial?.name || 'N/A'}</strong>
                {item.packingMaterial?.alias && <div className="small text-muted">({item.packingMaterial.alias})</div>}
              </div>
            </td>
            <td>{item.orderedQuantity}</td>
            <td>{item.unloadedQuantity}</td>
            {/* <td>
              <Badge bg={
                item.qualityStatus === 'Approved' ? 'success' :
                item.qualityStatus === 'Rejected' ? 'danger' :
                item.qualityStatus === 'On Hold' ? 'warning' : 'secondary'
              }>
                {item.qualityStatus}
              </Badge>
            </td> */}
            <td>{item.remarks || '-'}</td>
          </tr>
        )) || []}
      </tbody>
    </Table>
  );
};

const GRNEditModal = ({ show, onHide, grn, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formData, setFormData] = useState({
    grnDate: '',
    vehicleNumber: '',
    driverName: '',
    invoiceNumber: '',
    remarks: '',
    status: 'Draft'
  });

  useEffect(() => {
    if (grn && show) {
      setFormData({
        grnDate: grn.grnDate ? new Date(grn.grnDate).toISOString().split('T')[0] : '',
        vehicleNumber: grn.vehicleNumber || '',
        driverName: grn.driverName || '',
        invoiceNumber: grn.invoiceNumber || '',
        remarks: grn.remarks || '',
        status: grn.status || 'Draft'
      });
      setSelectedFiles([]);
    }
  }, [grn, show]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleDeleteDocument = async (documentIndex) => {
    if (!grn) return;

    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await axios.delete(`${config.API_BASE_URL}/pm-grn/${grn._id}/documents/${documentIndex}`);
        onUpdate();
        toast.success('Document deleted successfully');
      } catch (error) {
        console.error('Error deleting document:', error);
        toast.error('Failed to delete document');
      }
    }
  };

  const handleSubmit = async () => {
    if (!grn) return;

    if (!formData.vehicleNumber || !formData.driverName || !formData.invoiceNumber) {
      toast.error("Please fill vehicle number, driver name, and invoice number");
      return;
    }

    setLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('grnDate', formData.grnDate);
      formDataObj.append('vehicleNumber', formData.vehicleNumber);
      formDataObj.append('driverName', formData.driverName);
      formDataObj.append('invoiceNumber', formData.invoiceNumber);
      formDataObj.append('remarks', formData.remarks || '');
      formDataObj.append('status', formData.status);

      // Add new documents if selected
      if (selectedFiles.length > 0) {
        selectedFiles.forEach(file => {
          formDataObj.append('documents', file);
        });
      }

      await axios.put(`${config.API_BASE_URL}/pm-grn/${grn._id}`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success("GRN updated successfully");
      onHide();
      // Delay the update to prevent DOM manipulation conflicts during modal closing
      setTimeout(() => {
        onUpdate();
      }, 100);
    } catch (error) {
      console.error("Error updating GRN:", error);
      toast.error("Failed to update GRN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit GRN - {grn?.grnNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>GRN Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={formData.grnDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, grnDate: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="Draft">Draft</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Vehicle Number <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.vehicleNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehicleNumber: e.target.value }))}
                  placeholder="MH 02 KH 1234"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Driver Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.driverName}
                  onChange={(e) => setFormData(prev => ({ ...prev, driverName: e.target.value }))}
                  placeholder="John Doe"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Invoice Number <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                  placeholder="INV-2025-001"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Existing Documents */}
          {grn?.documents && grn.documents.length > 0 && (
            <div className="mb-3">
              <strong>Uploaded Documents:</strong>
              <ul className="list-unstyled mt-2">
                {grn.documents.map((doc, index) => (
                  <li key={index} className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file me-2 text-primary"></i>
                      <a
                        href={`${config.API_BASE_URL}${doc.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        {doc.filename}
                      </a>
                      {doc.uploadedAt && (
                        <small className="text-muted ms-2">
                          ({new Date(doc.uploadedAt).toLocaleDateString()})
                        </small>
                      )}
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteDocument(index)}
                      title="Delete document"
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Add More Documents</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={handleFileChange}
            />
            <Form.Text className="text-muted">
              Upload additional documents (PDF, PNG, JPG, DOC, DOCX)
            </Form.Text>
            {selectedFiles.length > 0 && (
              <small className="text-success mt-1 d-block">
                {selectedFiles.length} new file(s) selected for upload
              </small>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.remarks}
              onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
              placeholder="Additional remarks about the goods"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update GRN'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const CreateGRNModal = ({ show, onHide, pmPurchaseOrderId, order, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formData, setFormData] = useState({
    grnDate: new Date().toISOString().split('T')[0],
    vehicleNumber: '',
    driverName: '',
    invoiceNumber: '',
    remarks: ''
  });

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleSubmit = async () => {
    if (!formData.vehicleNumber || !formData.driverName || !formData.invoiceNumber) {
      toast.error("Please fill vehicle number, driver name, and invoice number");
      return;
    }

    setLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('pmPurchaseOrder', pmPurchaseOrderId);
      formDataObj.append('grnDate', formData.grnDate);
      formDataObj.append('vehicleNumber', formData.vehicleNumber);
      formDataObj.append('driverName', formData.driverName);
      formDataObj.append('invoiceNumber', formData.invoiceNumber);
      formDataObj.append('remarks', formData.remarks || '');

      // Add documents if selected
      if (selectedFiles.length > 0) {
        selectedFiles.forEach(file => {
          formDataObj.append('documents', file);
        });
      }

      await axios.post(`${config.API_BASE_URL}/pm-grn`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success("GRN created successfully");
      onHide();
      // Delay the update to prevent DOM manipulation conflicts during modal closing
      setTimeout(() => {
        onUpdate();
      }, 100);
    } catch (error) {
      console.error("Error creating GRN:", error);
      toast.error("Failed to create GRN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create GRN for PM PO #{order?.poNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>GRN Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={formData.grnDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, grnDate: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Vehicle Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.vehicleNumber}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      vehicleNumber: e.target.value.toUpperCase()
                    }))
                  }
                  placeholder="RJ19AB1234"
                  style={{ textTransform: "uppercase" }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Invoice Number <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                  placeholder="INV-2025-001"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Driver Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.driverName}
                  onChange={(e) => setFormData(prev => ({ ...prev, driverName: e.target.value }))}
                  placeholder="John Doe"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Document Upload</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleFileChange}
            />
            <Form.Text className="text-muted">
              Upload invoice copy, delivery note, or other related documents (PDF, PNG, JPG)
            </Form.Text>
            {selectedFiles.length > 0 && (
              <small className="text-success">{selectedFiles.length} file(s) selected</small>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.remarks}
              onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
              placeholder="Additional remarks about the goods"
            />
          </Form.Group>
        </Form>

        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          <strong>Note:</strong> After creating the GRN, go to the Inward Entries tab to receive materials into your warehouse. The GRN will be updated with received items and quantities.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create GRN'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default function PMPOGRNTab({ pmPurchaseOrderId, order, inwardEntries, onUpdate }) {
  const [grns, setGrns] = useState([]);
  const [qualityEntries, setQualityEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [showGRNModal, setShowGRNModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const canCreateGRN = useHasPermission(PERMISSIONS.QUALITY_CHECK_ADD);
  // const canViewGRN = useHasPermission(PERMISSIONS.QUALITY_CHECK_VIEW);
  const canEditGRN = useHasPermission(PERMISSIONS.GRN_EDIT);
  const canDeleteGRN = useHasPermission(PERMISSIONS.GRN_DELETE);

  const fetchGRNs = useCallback(async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/pm-grn?pmPurchaseOrder=${pmPurchaseOrderId}`);
      setGrns(response.data);
    } catch (error) {
      console.error("Error fetching GRNs:", error);
      toast.error("Failed to load GRNs");
    } finally {
      setLoading(false);
    }
  }, [pmPurchaseOrderId]);

  const fetchQualityEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/pm-quality-checks?pmPurchaseOrder=${pmPurchaseOrderId}`);
      setQualityEntries(response.data);
    } catch (error) {
      console.error("Error fetching quality entries:", error);
    }
  }, [pmPurchaseOrderId]);

  useEffect(() => {
    fetchGRNs();
    fetchQualityEntries();
  }, [pmPurchaseOrderId, fetchGRNs, fetchQualityEntries]);

  const handleCreateGRN = () => {
    if (!canCreateGRN) {
      toast.error("You don't have permission to create GRNs");
      return;
    }
    setShowCreateModal(true);
  };

  const handleViewGRN = (grn) => {
    setSelectedGRN(grn);
    setShowGRNModal(true);
  };

  const handleEditGRN = (grn) => {
    if (!canEditGRN) {
      toast.error("You don't have permission to edit GRNs");
      return;
    }

    if (hasQualityEntries(grn._id)) {
      toast.error("Cannot edit GRN as quality check entries already exist for this GRN");
      return;
    }

    setSelectedGRN(grn);
    setShowEditModal(true);
  };

  const handleDeleteGRN = async (grn) => {
    if (!canDeleteGRN) {
      toast.error("You don't have permission to delete GRNs");
      return;
    }

    if (hasQualityEntries(grn._id)) {
      toast.error("Cannot delete GRN as quality check entries already exist for this GRN");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete GRN ${grn.grnNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${config.API_BASE_URL}/pm-grn/${grn._id}`);
      toast.success("GRN deleted successfully");
      fetchGRNs();
      onUpdate();
    } catch (err) {
      console.error("Delete GRN error:", err);
      toast.error("Failed to delete GRN");
    }
  };

  const hasQualityEntries = (grnId) => {
    return qualityEntries.some(entry => entry.pmGRN === grnId || entry.pmGRN?._id === grnId);
  };

  if (loading) {
    return <div className="text-center py-4">Loading GRNs...</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">
            <i className="fas fa-file-invoice me-2"></i>Goods Receipt Notes (GRN)
          </h5>
          <small className="text-muted">Create receipt records when materials arrive, then add inward entries</small>
        </div>

        <PermissionGate require={PERMISSIONS.QUALITY_CHECK_ADD}>
          <Button
            variant="success"
            onClick={handleCreateGRN}
          >
            <i className="fas fa-plus me-2"></i>Create GRN
          </Button>
        </PermissionGate>
      </div>

      {grns.length > 0 ? (
        <div className="table-responsive">
          <Table bordered hover>
            <thead className="table-light">
              <tr>
                <th>GRN Number</th>
                <th>GRN Date</th>
                <th>Total Items</th>
                <th>Total Received</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {grns.map(grn => (
                <tr key={grn._id}>
                  <td className="fw-bold">{grn.grnNumber}</td>
                  <td>{new Date(grn.grnDate).toLocaleDateString()}</td>
                  <td>{grn.items?.length || 0}</td>
                  <td>
                    {grn.items?.reduce((sum, item) => sum + (item.unloadedQuantity || 0), 0) || 0}
                  </td>
                  <td><GRNStatusBadge status={grn.status} /></td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewGRN(grn)}
                      >
                        <i className="fas fa-eye me-1"></i>View Details
                      </Button>
                      <PermissionGate require={PERMISSIONS.QUALITY_CHECK_ADD}>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleEditGRN(grn)}
                          disabled={hasQualityEntries(grn._id)}
                          title={hasQualityEntries(grn._id) ? "Cannot edit GRN with existing quality checks" : "Edit GRN"}
                        >
                          <i className="fas fa-edit me-1"></i>Edit
                        </Button>
                      </PermissionGate>
                      <PermissionGate require={PERMISSIONS.GRN_DELETE}>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteGRN(grn)}
                          disabled={hasQualityEntries(grn._id)}
                          title={hasQualityEntries(grn._id) ? "Cannot delete GRN with existing quality checks" : "Delete GRN"}
                        >
                          <i className="fas fa-trash me-1"></i>Delete
                        </Button>
                      </PermissionGate>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-file-invoice fa-3x mb-3"></i>
          <h5>No GRNs Created</h5>
          <p>Create your first GRN by adding inward entries and quality checks.</p>
        </div>
      )}

      {/* Create GRN Modal */}
      <CreateGRNModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        pmPurchaseOrderId={pmPurchaseOrderId}
        order={order}
        inwardEntries={inwardEntries}
        onUpdate={() => {
          fetchGRNs();
          onUpdate();
        }}
      />

      {/* View GRN Details Modal */}
      <Modal show={showGRNModal} onHide={() => setShowGRNModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>GRN Details - {selectedGRN?.grnNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedGRN && (
            <>
              {/* GRN Summary */}
              <Row className="mb-3">
                <Col md={4}>
                  <strong>GRN Number:</strong> {selectedGRN.grnNumber}
                </Col>
                <Col md={4}>
                  <strong>Invoice Number:</strong> {selectedGRN.invoiceNumber || 'N/A'}
                </Col>
                <Col md={4}>
                  <strong>GRN Date:</strong> {new Date(selectedGRN.grnDate).toLocaleDateString()}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Vehicle Number:</strong> {selectedGRN.vehicleNumber || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Driver Name:</strong> {selectedGRN.driverName || 'N/A'}
                </Col>
              </Row>

              {/* Documents */}
              {selectedGRN.documents && selectedGRN.documents.length > 0 && (
                <div className="mb-3">
                  <strong>Uploaded Documents:</strong>
                  <ul className="list-unstyled mt-2">
                    {selectedGRN.documents.map((doc, index) => (
                      <li key={index} className="d-flex align-items-center">
                        <i className="fas fa-file me-2"></i>
                        <a href={`${config.API_BASE_URL}/${doc.path}`} target="_blank" rel="noopener noreferrer">
                          {doc.filename}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <GRNItemsTable items={selectedGRN.items} />
              {selectedGRN.remarks && (
                <Alert variant="info" className="mt-3">
                  <strong>Remarks:</strong> {selectedGRN.remarks}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGRNModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit GRN Modal */}
      <GRNEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        grn={selectedGRN}
        onUpdate={() => {
          fetchGRNs();
          if (showGRNModal) {
            // If GRN details modal is open, close it since data has changed
            setShowGRNModal(false);
          }
          onUpdate();
        }}
      />
    </div>
  );
}
